/**
 * Dual bag/wishlist events (WebX data layer + FX mirror).
 */
import {trackOptimizelyEvent} from './optimizely-event'
import {categoriesFromList} from './optimizely-exp-page-context'

const currentUrl = () => (typeof window !== 'undefined' ? window.location.href : undefined)

/** Units for the quantity twin: finite and > 0, otherwise 1. */
const unitsFromQuantity = (quantity) => {
    const n = Number(quantity)
    return Number.isFinite(n) && n > 0 ? n : 1
}

/**
 * Twin of add/remove_from_bag: tags.value is units so WebX can sum them.
 * Price stays on the parent event.
 */
const trackBagQuantityEvent = (eventName, {sku, quantity, source}) => {
    const units = unitsFromQuantity(quantity)
    trackOptimizelyEvent({
        eventName,
        tags: {value: units},
        properties: {
            SKU: sku,
            URL: currentUrl(),
            quantity: units,
            source
        }
    })
}

/**
 * Category, Subcategory, length and single_size for the bag events, derived
 * from a SCAPI product or basket line (no backend hook sets these).
 * @param {Object} [product]
 * @returns {{category?: string, subcategory?: string, length?: boolean, singleSize?: boolean}}
 */
export const describeProductForOptimizely = (product) => {
    if (!product || typeof product !== 'object') return {}
    const attrs = Array.isArray(product.variationAttributes) ? product.variationAttributes : []
    const selected =
        product.variationValues && typeof product.variationValues === 'object'
            ? product.variationValues
            : null
    const byId = (id) => attrs.find((attr) => String(attr?.id || '').toLowerCase() === id)
    const {Category, Subcategory} = categoriesFromList(product.c_categories)

    // single_size = one size to pick (accessories), not one size in stock.
    // Unknown when the object carries neither the attributes nor a selection.
    let length
    let singleSize
    if (attrs.length) {
        const size = byId('size')
        length = !!byId('length')
        singleSize = !size || (size.values || []).length === 1
    } else if (selected) {
        length = 'length' in selected
        singleSize = !('size' in selected)
    }
    return {
        category: Category || product.c_primaryCategory || product.primaryCategoryId,
        subcategory: Subcategory,
        length,
        singleSize
    }
}

/**
 * Track an "add_to_bag" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.subcategory] - Product subcategory.
 * @param {boolean} [params.length] - Product has a length variation attribute.
 * @param {boolean} [params.singleSize] - Product has one size (or none).
 * @param {string} [params.source] - UI surface that triggered the ATB (e.g. "quick-view").
 * @param {number} [params.quantity] - Units added. Twin `add_to_bag_quantity` uses this as tags.value.
 */
export const trackAddToBagEvent = ({
    product,
    sku,
    variationGroupId,
    value,
    category,
    subcategory,
    length,
    singleSize,
    source,
    quantity
} = {}) => {
    const derived = describeProductForOptimizely(product)
    trackOptimizelyEvent({
        eventName: 'add_to_bag',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category ?? derived.category,
            Subcategory: subcategory ?? derived.subcategory,
            length: length ?? derived.length,
            single_size: singleSize ?? derived.singleSize,
            source,
            URL: currentUrl()
        }
    })
    trackBagQuantityEvent('add_to_bag_quantity', {sku, quantity, source})
}

/**
 * Track an "remove_from_bag" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.subcategory] - Product subcategory.
 * @param {boolean} [params.length] - Product has a length variation attribute.
 * @param {boolean} [params.singleSize] - Product has one size (or none).
 * @param {string} [params.source] - UI surface that triggered the ATB (e.g. "quick-view").
 * @param {number} [params.quantity] - Units removed (line qty, not a stepper delta). Twin `remove_from_bag_quantity` uses this as tags.value.
 */
export const trackRemoveFromBagEvent = ({
    product,
    sku,
    variationGroupId,
    value,
    category,
    subcategory,
    length,
    singleSize,
    source,
    quantity
} = {}) => {
    const derived = describeProductForOptimizely(product)
    trackOptimizelyEvent({
        eventName: 'remove_from_bag',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category ?? derived.category,
            Subcategory: subcategory ?? derived.subcategory,
            length: length ?? derived.length,
            single_size: singleSize ?? derived.singleSize,
            source,
            URL: currentUrl()
        }
    })
    trackBagQuantityEvent('remove_from_bag_quantity', {sku, quantity, source})
}

/**
 * Track an "add_to_wishlist" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.source] - UI surface that triggered the AWL (e.g. "pdp").
 */
export const trackAddToWishlistEvent = ({sku, variationGroupId, value, category, source} = {}) => {
    trackOptimizelyEvent({
        eventName: 'add_to_wishlist',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}

/**
 * Track an "remove_from_wishlist" event with explicit parameters.
 *
 * `URL` is captured automatically from the current page and is not a parameter.
 *
 * @param {Object} params
 * @param {string} [params.sku] - Product variant ID / SKU.
 * @param {string} [params.variationGroupId] - Variation group identifier.
 * @param {number} [params.value] - Product price as a float.
 * @param {string} [params.category] - Product category.
 * @param {string} [params.source] - UI surface that triggered the AWL (e.g. "pdp").
 */
export const trackRemoveFromWishlistEvent = ({
    sku,
    variationGroupId,
    value,
    category,
    source
} = {}) => {
    trackOptimizelyEvent({
        eventName: 'remove_from_wishlist',
        tags: {
            value
        },
        properties: {
            SKU: sku,
            variationGroupId,
            Category: category,
            source,
            URL: typeof window !== 'undefined' ? window.location.href : undefined
        }
    })
}
